/**
 * Dispatcher Operations Hooks
 * React Query mutation hooks for dispatcher-specific job creation and approval workflows
 *
 * Following DDD Principles:
 * - Mutations call backend APIs
 * - Backend owns all business logic (pricing, approval thresholds, permissions)
 * - Frontend just triggers actions and updates UI
 * - Invalidate queries to refresh data after mutations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceProviderService } from '@/services/service-provider.service';
import { jobKeys } from './useJobData';
import { toast } from 'sonner';

/**
 * Input for creating a job as dispatcher
 * Matches backend CreateJobAsDispatcherSchema
 */
export interface CreateJobAsDispatcherInput {
  // Service Details
  serviceType: string;
  containerSize: string;
  materialType: string;
  scheduledDate: string; // ISO datetime
  timeWindow?: string; // 'morning' | 'afternoon' | 'evening' | 'anytime'
  priority?: string; // 'standard' | 'urgent'

  // Location (required)
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    latitude?: number;
    longitude?: number;
  };

  // Customer snapshot (required)
  customerSnapshot: {
    customerId?: string;
    name: string;
    phone: string;
    email?: string;
  };

  // Notes
  specialInstructions?: string;
}

/**
 * Job with approval status
 * Extends base job type with price adjustment details
 */
export interface JobPendingApproval {
  id: string;
  project_name: string;
  project_address: string;
  scheduled_date: string;
  status: 'pending_price_approval';
  created_by_user_id: string;
  created_by_user_name?: string;
  base_price: number;
  adjusted_price: number;
  deviation_percentage: number;
  price_adjustments: Array<{
    lineItem: string;
    originalAmount: number;
    adjustedAmount: number;
    reason: string;
  }>;
  created_at: string;
}

/**
 * Pending approvals response
 */
export interface PendingApprovalsResponse {
  jobs: JobPendingApproval[];
  total: number;
}

/**
 * Query key factory for dispatcher operations
 */
export const dispatcherKeys = {
  all: ['dispatcher'] as const,
  approvals: () => [...dispatcherKeys.all, 'pending-approvals'] as const,
};

/**
 * Hook to create a job as dispatcher
 *
 * Features:
 * - Price adjustments with automatic approval detection
 * - Resource assignment at creation
 * - Override controls for constraints
 * - Invalidates job list after creation
 * - Shows appropriate toast based on approval status
 */
export function useCreateJobAsDispatcher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateJobAsDispatcherInput) =>
      serviceProviderService.createJobAsDispatcher(input),
    onSuccess: (job) => {
      // Invalidate job lists to refresh data
      queryClient.invalidateQueries({ queryKey: jobKeys.all });

      // Show appropriate message based on job status
      if (job.status === 'pending_price_approval') {
        toast.success(
          'Job created - pending manager approval for price adjustment',
          {
            description: 'A manager will need to approve the price before this job can be scheduled.',
          }
        );
      } else {
        toast.success('Job created successfully', {
          description: 'The job has been added to your schedule.',
        });
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to create job: ${error.message}`);
    },
  });
}

/**
 * Hook to approve or reject a price adjustment
 *
 * Features:
 * - Manager-only operation
 * - Updates job status based on decision
 * - Invalidates both job and approval queries
 * - Shows success/error toast
 */
export function useApprovePriceAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      jobId,
      decision,
      notes,
    }: {
      jobId: string;
      decision: 'approve' | 'reject';
      notes?: string;
    }) => serviceProviderService.approvePriceAdjustment(jobId, decision, notes),
    onSuccess: (_, { decision }) => {
      // Invalidate job queries to refresh data
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
      queryClient.invalidateQueries({ queryKey: dispatcherKeys.approvals() });

      // Show success message
      if (decision === 'approve') {
        toast.success('Price adjustment approved', {
          description: 'The job has been scheduled with the adjusted price.',
        });
      } else {
        toast.success('Price adjustment rejected', {
          description: 'The dispatcher has been notified to revise the pricing.',
        });
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to process approval: ${error.message}`);
    },
  });
}

/**
 * Hook to get jobs pending price approval
 *
 * Features:
 * - Manager-only query
 * - Cached for 1 minute
 * - Auto-refetch on window focus
 * - Shows toast on error
 */
export function usePendingApprovals() {
  return useQuery<PendingApprovalsResponse, Error>({
    queryKey: dispatcherKeys.approvals(),
    queryFn: () => serviceProviderService.getPendingApprovals(),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });
}
